import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircle, Download, FileIcon, FileText, Image, Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { cn } from '@/lib/utils'
import type { Asset } from '@/types/assets'

interface PreviewDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  asset: Asset | null
}

function getFileIcon(contentType: string) {
  if (contentType.startsWith('image/')) {
    return <Image className="h-16 w-16 text-green-500" />
  }
  if (contentType === 'application/pdf') {
    return <FileText className="h-16 w-16 text-red-500" />
  }
  if (contentType.includes('spreadsheet') || contentType === 'text/csv') {
    return <FileText className="h-16 w-16 text-emerald-500" />
  }
  if (contentType.includes('document') || contentType === 'text/plain' || contentType === 'text/markdown') {
    return <FileText className="h-16 w-16 text-blue-500" />
  }
  return <FileIcon className="h-16 w-16 text-gray-500" />
}

function getFileType(contentType: string): string {
  if (contentType.startsWith('image/')) return 'Image'
  if (contentType === 'application/pdf') return 'PDF'
  if (contentType.includes('spreadsheet')) return 'Spreadsheet'
  if (contentType === 'text/csv') return 'CSV'
  if (contentType.includes('document')) return 'Document'
  if (contentType === 'text/plain') return 'Text'
  if (contentType === 'text/markdown') return 'Markdown'
  if (contentType === 'application/json') return 'JSON'
  return 'File'
}

export function PreviewDialog({ open, onOpenChange, asset }: PreviewDialogProps) {
  const { t } = useTranslation('assets')
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    if (open) {
      setIsLoading(true)
      setHasError(false)
    }
  }, [asset?.id, open])

  if (!asset) return null

  const handleDownload = () => {
    const link = document.createElement('a')
    link.href = asset.file_url
    link.download = asset.name
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const isImage = asset.is_image
  const isPdf = asset.content_type === 'application/pdf'
  const previewUrl = asset.preview_url || asset.file_url

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0 gap-0 overflow-hidden">
        <VisuallyHidden>
          <DialogTitle>Preview: {asset.name}</DialogTitle>
        </VisuallyHidden>

        {/* Header */}
        <div className="flex items-center justify-between pl-4 pr-12 py-3 border-b bg-background">
          <div className="flex items-center gap-3 min-w-0">
            <span className="font-medium truncate" title={asset.name}>
              {asset.name}
            </span>
            <span className="text-sm text-muted-foreground shrink-0">
              {asset.human_byte_size}
            </span>
          </div>
          <Button variant="outline" size="sm" onClick={handleDownload} className="shrink-0">
            <Download className="h-4 w-4 mr-2" />
            {t('buttons.download')}
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto bg-black/90 flex items-center justify-center min-h-[400px] relative">
          {isLoading && (isImage || isPdf) && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-white" />
            </div>
          )}
          {hasError ? (
            <div className="flex flex-col items-center gap-4 p-8 text-white">
              <AlertCircle className="h-16 w-16 text-red-400" />
              <div className="text-center">
                <p className="font-medium text-lg">{t('preview.errorTitle')}</p>
                <p className="text-sm text-gray-400 mt-1">
                  {t('preview.errorDescription')}
                </p>
              </div>
              <Button onClick={handleDownload} className="mt-4">
                <Download className="h-4 w-4 mr-2" />
                {t('buttons.downloadFile')}
              </Button>
            </div>
          ) : isImage ? (
            <img
              src={previewUrl}
              alt={asset.name}
              className={cn("max-w-full max-h-[calc(90vh-80px)] object-contain", isLoading && "invisible")}
              onLoad={() => setIsLoading(false)}
              onError={() => { setIsLoading(false); setHasError(true) }}
            />
          ) : isPdf ? (
            <iframe
              src={asset.file_url}
              title={asset.name}
              className={cn("w-full h-[calc(90vh-80px)] bg-white", isLoading && "invisible")}
              onLoad={() => setIsLoading(false)}
              onError={() => { setIsLoading(false); setHasError(true) }}
            />
          ) : (
            <div className="flex flex-col items-center gap-4 p-8 text-white">
              {getFileIcon(asset.content_type)}
              <div className="text-center">
                <p className="font-medium text-lg">{asset.name}</p>
                <p className="text-sm text-gray-400 mt-1">
                  {getFileType(asset.content_type)} - {asset.human_byte_size}
                </p>
              </div>
              <Button onClick={handleDownload} className="mt-4">
                <Download className="h-4 w-4 mr-2" />
                {t('buttons.downloadFile')}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
